import { Component, h, State, Event, EventEmitter, Prop, Element } from '@stencil/core';

@Component({
  tag: 'biel-input',
  styleUrl: 'biel-input.css',
  shadow: true
})
export class BielInput {

  @State() userInput: string = '';
  @Event() messageSent: EventEmitter<{ bielId: string, message: string }>;
  @Event() textareaHeightChanged: EventEmitter<number>;
  @Event() scrollToBottom: EventEmitter<{ bielId: string }>;

  @Prop() placeholderText: string = 'Type your message';
  @Prop() sendButtonText: string = 'Send';
  @Prop() isDisabled: boolean = false;
  @Prop() bielId: string;

  @Element() el: HTMLElement;

  componentDidLoad() {
    const textarea = this.el.shadowRoot.querySelector('textarea');
    if (textarea) {
      textarea.focus();
    }
  }

  handleInputChange(event) {
    this.userInput = event.target.value;
    this.adjustTextareaHeight();
  }

  handleSendMessage() {
    if (this.userInput.trim()) {
      this.messageSent.emit({ bielId: this.bielId, message: this.userInput });
      this.userInput = '';
      this.resetTextareaHeight();
      this.scrollToBottom.emit({ bielId: this.bielId });
    }
  }

  handleKeyPress(event) {
    if (event.key === 'Enter' && !event.shiftKey && !this.isDisabled) {
      event.preventDefault();
      this.handleSendMessage();
    }
  }

  getCSSVariable(element: HTMLElement, variableName: string): string {
    return getComputedStyle(element).getPropertyValue(variableName);
  }

  getPixelValue(element: HTMLElement, variableName: string): number {
    const value = this.getCSSVariable(element, variableName);
    const numValue = parseFloat(value.replace('px', ''));
    return Math.round(numValue);
  }

  adjustTextareaHeight() {
    const textarea = this.el.shadowRoot.querySelector('textarea');
    const minHeight = this.getCSSVariable(textarea, 'min-height');
    const maxHeight = this.getPixelValue(textarea, 'max-height');
    const padding = this.getPixelValue(textarea, 'padding') * 2;

    const previousHeight = this.getPixelValue(textarea, 'height');
    textarea.style.height = minHeight;
    const newHeight = Math.min(textarea.scrollHeight - padding, maxHeight);
    textarea.style.height = `${Math.round(newHeight)}px`;
    textarea.style.overflowY = newHeight >= maxHeight ? 'visible' : 'hidden';

    if (newHeight !== previousHeight) {
      this.textareaHeightChanged.emit(newHeight - previousHeight);
    }
  }

  resetTextareaHeight() {
    const textarea = this.el.shadowRoot.querySelector('textarea');
    const minHeight = this.getPixelValue(textarea, 'min-height');
    const previousHeight = this.getPixelValue(textarea, 'height');
    textarea.style.height = `${minHeight}px`;
    textarea.style.overflowY = 'hidden';
    this.textareaHeightChanged.emit(minHeight - previousHeight);
  }

  componentDidUpdate() {
    const textarea = this.el.shadowRoot.querySelector('textarea');
    if (!this.isDisabled) {
      textarea.focus();
    }
  }

  render() {
    return (
      <div class="biel-input-container">
        <textarea
          class="biel-input__textarea"
          placeholder={this.placeholderText}
          value={this.userInput}
          onInput={(event) => this.handleInputChange(event)}
          onKeyPress={(event) => this.handleKeyPress(event)}
          disabled={this.isDisabled}
        ></textarea>
        <button
          class="biel-input__button"
          onClick={() => this.handleSendMessage()}
          disabled={this.isDisabled}
        >
          {this.sendButtonText}
        </button>
      </div>
    );
  }
}